import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const zones = [
  {
    id: 1,
    title: 'Outdoor to Main Entrance',
    subtitle: 'Zone 1 Defense',
    description: 'First line of defense against dirt, debris, and moisture. Heavy-duty entry systems that protect interior floors from day one.',
    image: '/Assets/images/01_Homepage/feature_outdoor-to-main-entrance_v2.jpg',
    href: '/products?zones=1-2,1-2-3',
    badge: 'Zone 1'
  },
  {
    id: 2,
    title: 'Main Entrance to Vestibules',
    subtitle: 'Zone 2 Defense',
    description: 'Transitional flooring that continues the scraping action while adding moisture absorption. The critical second barrier.',
    image: '/Assets/images/01_Homepage/zone2_main-to-vestibules.jpg',
    href: '/products?zones=1-2,2-3,1-2-3',
    badge: 'Zone 2'
  },
  {
    id: 3,
    title: 'Retail Floor to Back of House',
    subtitle: 'Zone 3 Defense',
    description: 'Interior flooring designed for beauty and performance. Engineered to handle moisture challenges while enhancing your brand image.',
    image: '/Assets/images/01_Homepage/zone3_lobby-to-back.jpg',
    href: '/products?zones=2-3,1-2-3',
    badge: 'Zone 3'
  },
  {
    id: 4,
    title: 'Discover How Your Floors Shape Your Brand',
    subtitle: 'Quick Assessment',
    description: 'Take our quick assessment to find the perfect flooring solution for your space.',
    image: '/Assets/images/01_Homepage/discover_brand_quiz.jpg',
    imagePosition: '30% center',
    href: 'https://george-jz9qhyvo.scoreapp.com',
    badge: null,
    isExternal: true,
    linkText: 'Take the Quiz'
  }
];

export const ZoneSelectorSection = () => {
  return (
    <section className="section-padding bg-muted">
      <div className="container-portico">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
            Flooring Made for You
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Our Zone Defense System provides comprehensive protection from the outside in. 
            Each zone is engineered for specific challenges.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {zones.map((zone, index) => {
            const CardWrapper = zone.isExternal ? 'a' : Link;
            const wrapperProps = zone.isExternal 
              ? { href: zone.href, target: '_blank', rel: 'noopener noreferrer' }
              : { to: zone.href };
            
            return (
              <motion.div
                key={zone.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="h-full"
              >
                <CardWrapper {...wrapperProps} className="block group h-full">
                  <div className="card-portico overflow-hidden h-full flex flex-col">
                    <div className="relative h-36 overflow-hidden flex-shrink-0">
                      <img
                        src={zone.image}
                        alt={zone.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-smooth"
                        style={zone.imagePosition ? { objectPosition: zone.imagePosition } : undefined}
                      />
                      {zone.badge && (
                        <div className="absolute top-2 left-2 bg-gray-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-white shadow-md">
                          {zone.badge}
                        </div>
                      )}
                    </div>
                    <div className="p-3 flex flex-col flex-grow">
                      <p className="text-[10px] text-muted-foreground mb-0.5">{zone.subtitle}</p>
                      <h3 className="font-heading font-semibold text-sm mb-1.5 group-hover:text-primary transition-colors leading-tight">
                        {zone.title}
                      </h3>
                      <p className="text-[10px] text-muted-foreground mb-2 line-clamp-2 flex-grow">
                        {zone.description}
                      </p>
                      <span className="text-primary font-semibold text-[10px] flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                        {zone.linkText || 'Explore Products'}
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </CardWrapper>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ZoneSelectorSection;
